import { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { formatDateTime } from "@/components/admin/ui";
import { SERVICE_AREA_STATUS_META } from "./salesMeta";
import { LeadServiceAreaMap } from "./LeadServiceAreaMap";
import { TriangleAlert, CheckCircle2 } from "lucide-react";

const ActionButton = ({ onClick, busy, disabled, variant = "primary", children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy || disabled}
    data-testid={testId}
    className={`rounded-xl border px-3.5 py-2 text-xs font-bold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${
      variant === "primary"
        ? "border-transparent bg-[#0052FF] text-white hover:bg-[#0040CC]"
        : "border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
    }`}
  >
    {children}
  </button>
);

const SectionLabel = ({ children }) => (
  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{children}</p>
);

export const LeadServiceAreaTab = ({ lead, meta, eligibleTeams, eligibleLeadOwners, onRefresh, onError, API }) => {
  const [busy, setBusy] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressOverride, setAddressOverride] = useState("");
  const [teamPick, setTeamPick] = useState("");
  const [ownerPick, setOwnerPick] = useState("");
  const [otherAreaPick, setOtherAreaPick] = useState("");

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await onRefresh(); } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const verify = (override) => run(() => axios.post(`${API}/org/sales/leads/${lead.lead_id}/service-area/verify`, { address_override: override || null }, { withCredentials: true }));
  const selectArea = (serviceAreaId) => run(() => axios.post(`${API}/org/sales/leads/${lead.lead_id}/service-area/select`, { service_area_id: serviceAreaId }, { withCredentials: true }));
  const assignTeam = (teamId) => run(() => axios.post(`${API}/org/sales/leads/${lead.lead_id}/assign`, { team_id: teamId }, { withCredentials: true }));
  const assignOwner = (userId) => run(() => axios.post(`${API}/org/sales/leads/${lead.lead_id}/assign`, { lead_owner_user_id: userId }, { withCredentials: true }));

  const verification = lead.service_address_verification;
  const statusMeta = verification ? SERVICE_AREA_STATUS_META[verification.status] : null;
  const addr = lead.service_address;
  const recommendedId = verification?.recommended_service_area_id || null;
  const recommendedMatch = verification?.matches?.find((m) => m.service_area_id === recommendedId) || null;
  const isRecommendationAccepted = lead.confirmed_service_area_id && recommendedId && lead.confirmed_service_area_id === recommendedId && !lead.service_area_is_override;
  const otherActiveAreas = (meta.service_areas || []).filter((a) => a.service_area_id !== lead.confirmed_service_area_id);

  return (
    <div className="space-y-6" data-testid="lead-tab-panel-service-area">
      {/* 1. CUSTOMER ADDRESS */}
      <div>
        <SectionLabel>Customer Service Address</SectionLabel>
        {addr ? (
          <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white" data-testid="lead-sa-address">
            {addr.street}{addr.unit ? `, ${addr.unit}` : ""}<br />{addr.city}, {addr.state} {addr.zip_code}
          </p>
        ) : (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400" data-testid="lead-sa-address">No service address on file</p>
        )}

        {/* 2. ADDRESS VERIFICATION */}
        {verification ? (
          <p className={`mt-2 text-sm font-bold ${statusMeta?.cls}`} data-testid="lead-sa-status">{statusMeta?.label}</p>
        ) : (
          <p className="mt-2 text-sm font-semibold text-slate-400" data-testid="lead-sa-status">Not yet verified</p>
        )}
        {verification && (
          <p className="mt-1 text-xs text-slate-400" data-testid="lead-sa-verified-meta">
            {verification.formatted_address} · Verified by {verification.verified_by_name} · {formatDateTime(verification.verified_at)}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <ActionButton onClick={() => verify()} busy={busy} disabled={!addr && !addressOverride} testId="lead-sa-verify-btn">
            {verification ? "Re-verify Address" : "Verify Address"}
          </ActionButton>
          <button type="button" onClick={() => setEditingAddress((v) => !v)} data-testid="lead-sa-edit-address-toggle" className="text-xs font-bold text-[#0052FF] hover:underline">
            {editingAddress ? "Cancel correction" : "Address changed? Verify a corrected address"}
          </button>
        </div>
        {editingAddress && (
          <div className="mt-2 flex gap-2">
            <input value={addressOverride} onChange={(e) => setAddressOverride(e.target.value)} placeholder="Enter corrected full address…" data-testid="lead-sa-address-override-input" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
            <ActionButton onClick={() => verify(addressOverride)} busy={busy} disabled={!addressOverride.trim()} testId="lead-sa-verify-override-btn">Verify</ActionButton>
          </div>
        )}
      </div>

      {verification && (
        <LeadServiceAreaMap
          matches={verification.matches}
          customer={{ lat: verification.lat, lng: verification.lng }}
          selectedId={lead.confirmed_service_area_id}
          recommendedId={recommendedId}
        />
      )}

      {/* 3. RECOMMENDED SERVICE AREA */}
      {verification && (
        <div className="border-t border-slate-200 pt-4 dark:border-white/10">
          <SectionLabel>Recommended Service Area</SectionLabel>
          {verification.status === "outside" && (
            <div className="mt-2 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400" data-testid="lead-sa-outside-banner">
              <TriangleAlert size={16} className="mt-0.5 shrink-0" />
              <div>
                <p>OUTSIDE CURRENT SERVICE AREAS</p>
                <p className="mt-0.5 font-normal">This verified customer address does not fall within an active Service Area.</p>
              </div>
            </div>
          )}
          {verification.status === "ambiguous" && (
            <div className="mt-2 space-y-2" data-testid="lead-sa-ambiguous-matches">
              <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">Multiple areas matched — choose one manually:</p>
              {verification.matches.map((m) => (
                <div key={m.service_area_id} className="flex items-center justify-between rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`lead-sa-match-${m.service_area_id}`}>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{m.name}</p>
                  <ActionButton onClick={() => selectArea(m.service_area_id)} busy={busy} variant="secondary" testId={`lead-sa-select-${m.service_area_id}`}>Select</ActionButton>
                </div>
              ))}
            </div>
          )}
          {verification.status === "matched" && recommendedMatch && (
            <div className="mt-2 flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-500/30 dark:bg-emerald-500/10" data-testid="lead-sa-recommended">
              <div>
                <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">
                  {recommendedMatch.name} <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">✓ Recommended</span>
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">Customer address falls within this Service Area.</p>
              </div>
              {isRecommendationAccepted ? (
                <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400">Accepted</span>
              ) : (
                <ActionButton onClick={() => selectArea(recommendedId)} busy={busy} testId="lead-sa-use-recommended-btn">Use Recommended Area</ActionButton>
              )}
            </div>
          )}

          <div className="mt-3 flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Or choose a different active Service Area (manual override):</label>
              <select value={otherAreaPick} onChange={(e) => setOtherAreaPick(e.target.value)} data-testid="lead-sa-other-area-select" className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                <option value="">Choose an active Service Area…</option>
                {otherActiveAreas.map((a) => <option key={a.service_area_id} value={a.service_area_id}>{a.name}</option>)}
              </select>
            </div>
            <ActionButton onClick={() => { selectArea(otherAreaPick); setOtherAreaPick(""); }} busy={busy} disabled={!otherAreaPick} variant="secondary" testId="lead-sa-other-area-select-btn">Override</ActionButton>
          </div>
        </div>
      )}

      {/* 4. SELECTED SERVICE AREA */}
      <div className="border-t border-slate-200 pt-4 dark:border-white/10">
        <SectionLabel>Selected Service Area</SectionLabel>
        {lead.confirmed_service_area_id ? (
          <div className="mt-2 flex items-center justify-between rounded-xl border border-[#0052FF] bg-blue-50 p-3 dark:border-[#0052FF]/40 dark:bg-[#0052FF]/10" data-testid="lead-sa-confirmed">
            <p className="flex items-center gap-2 text-sm font-bold text-[#0052FF]">
              <CheckCircle2 size={16} /> {lead.confirmed_service_area_name} <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-[#0052FF] dark:bg-[#0052FF]/20">✓ Selected</span>
              {lead.service_area_is_override && recommendedMatch && (
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">(overridden — originally recommended: {recommendedMatch.name})</span>
              )}
            </p>
            <button type="button" onClick={() => selectArea(null)} disabled={busy} data-testid="lead-sa-clear-btn" className="text-xs font-bold text-red-600 hover:underline dark:text-red-400">Clear</button>
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400" data-testid="lead-sa-no-selection">No Service Area selected yet.</p>
        )}
      </div>

      {/* 5. TEAM (eligible for Selected Service Area only) */}
      <div className="border-t border-slate-200 pt-4 dark:border-white/10">
        <SectionLabel>Team</SectionLabel>
        {!lead.confirmed_service_area_id ? (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400" data-testid="lead-sa-team-no-area">Select a Service Area above to see teams available for it.</p>
        ) : eligibleTeams.length === 0 ? (
          <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400" data-testid="lead-sa-team-warning">
            <TriangleAlert size={16} className="mt-0.5 shrink-0" /> No active team is currently assigned to this Service Area.
          </div>
        ) : eligibleTeams.length === 1 ? (
          <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid="lead-sa-team-single">
            <p className="text-sm font-bold text-slate-900 dark:text-white">{eligibleTeams[0].name}</p>
            {lead.team_id === eligibleTeams[0].team_id ? (
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">Assigned</span>
            ) : (
              <ActionButton onClick={() => assignTeam(eligibleTeams[0].team_id)} busy={busy} testId="lead-sa-team-single-assign">Assign</ActionButton>
            )}
          </div>
        ) : (
          <div className="mt-2 flex items-end gap-2">
            <div className="flex-1">
              <select value={teamPick} onChange={(e) => setTeamPick(e.target.value)} data-testid="lead-sa-team-select" className="block w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                <option value="">Choose a team eligible for this area…</option>
                {eligibleTeams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}{lead.team_id === t.team_id ? " (current)" : ""}</option>)}
              </select>
            </div>
            <ActionButton onClick={() => assignTeam(teamPick)} busy={busy} disabled={!teamPick} testId="lead-sa-team-select-assign">Assign</ActionButton>
          </div>
        )}
      </div>

      {/* 6. LEAD OWNER (eligible active Team Leads of the selected Team only) */}
      <div className="border-t border-slate-200 pt-4 dark:border-white/10">
        <SectionLabel>Lead Owner</SectionLabel>
        {!lead.team_id ? (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400" data-testid="lead-sa-owner-no-team">Select a Team above to see eligible Lead Owners.</p>
        ) : eligibleLeadOwners.length === 0 ? (
          <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400" data-testid="lead-sa-owner-warning">
            <TriangleAlert size={16} className="mt-0.5 shrink-0" />
            <div>
              <p>NO ELIGIBLE LEAD OWNER</p>
              <p className="mt-0.5 font-normal">The selected Team does not currently have an active Team Lead.</p>
            </div>
          </div>
        ) : eligibleLeadOwners.length === 1 ? (
          <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid="lead-sa-owner-single">
            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white">{eligibleLeadOwners[0].name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Team Lead — {lead.team_name}</p>
            </div>
            {lead.lead_owner_user_id === eligibleLeadOwners[0].user_id ? (
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">Assigned</span>
            ) : (
              <ActionButton onClick={() => assignOwner(eligibleLeadOwners[0].user_id)} busy={busy} testId="lead-sa-owner-single-assign">Assign</ActionButton>
            )}
          </div>
        ) : (
          <div className="mt-2 flex items-end gap-2">
            <div className="flex-1">
              <select value={ownerPick} onChange={(e) => setOwnerPick(e.target.value)} data-testid="lead-sa-owner-select" className="block w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                <option value="">Choose a Team Lead…</option>
                {eligibleLeadOwners.map((o) => <option key={o.user_id} value={o.user_id}>{o.name} — Team Lead{lead.lead_owner_user_id === o.user_id ? " (current)" : ""}</option>)}
              </select>
            </div>
            <ActionButton onClick={() => assignOwner(ownerPick)} busy={busy} disabled={!ownerPick} testId="lead-sa-owner-select-assign">Assign</ActionButton>
          </div>
        )}
      </div>
    </div>
  );
};
